import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import {
  Form,
  Field,
  useFieldArray,
  useWatch,
  WatchField,
  useForm,
  isEmpty
} from '../../../../form'
import Button from '@material-ui/core/Button'
import MenuItem from '@material-ui/core/MenuItem'
import InvoiceInputTable from '../../invoiceInputTable.component'
import { withStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import mobileView from '../../../../utils/mobile.utils'
import Snackbar from '@material-ui/core/Snackbar'
import {
  getInvoiceUserEmail,
  getUserCompanyUpdateInfo,
  changeVATPercent,
  getJournal
} from '../../../../actions'
import { invoiceProductsAndServicesValidate } from '../../../../containers/validate'
import { calculateVat } from '../../../../utils/invoice.utils'
import AddCostContainer from '../../../../containers/invoice/addCostForm.container'
import AddAllowanceContainer from '../../../../containers/invoice/addAllowanceForm.container'
import { MySnackbarContentWrapper } from '../../../../utils/snackbar.utils'
import InvoiceInputMobileComponent from '../../invoiceInputMobile.component'
import WorkshiftJournalContainer from '../../../../containers/invoice/workshiftJournal.container'

const styles = (theme) => ({
  switch: {
    '& .MuiSwitch-track': {
      backgroundColor: '#6fb3ac'
    },
    '& .MuiSwitch-switchBase': {
      color: '#44c0cc'
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#F67131'
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#cb7aa2'
    },
    '& .MuiSwitch-colorSecondary.Mui-disabled': {
      color: '#bdbdbd'
    },
    color: 'black',
    fontWeight: '600'
  },
  select2: {
    fontSize: '14px',
    cursor: 'pointer',
    border: '1px solid gray',
    width: '34%',
    margin: '0 10px 0 28px',
    [theme.breakpoints.down('900')]: {
      width: '50%'
    },
    [theme.breakpoints.down('500')]: {
      marginLeft: '15px',
      marginRight: '15px',
      width: 'auto'
    }
  },
  resize: {
    fontSize: '14px',
    padding: '-8px'
  },
  switches: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '50px',
    padding: '20px',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      gap: '0',
      padding: '0'
    }
  },
  button: {
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
    borderRadius: '10px'
  },
  buttonBack: {
    padding: !mobileView ? '2px 30px' : '4px 20px',
    color: 'rgb(83 77 77)',
    backgroundColor: '#fff',
    border: '2px solid #44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#E0F7FA'
    }
  },
  buttonNext: {
    padding: !mobileView ? '0 25px' : '2px 20px',
    color: '#fff',
    backgroundColor: '#44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#3BA9B5'
    }
  }
})

const InvoicedProducts = ({
  classes,
  goBack,
  goNext,
  formValues,
  disable_building_service,
  changeCustomerType
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { registeredUsersList, getRegisteredUserSuccess } = useSelector(
    (state) => state.invoice,
    shallowEqual
  )
  const isEdit = useSelector((state) => state.invoice.isEdit)
  const attachedWorkShiftJournalIds = useSelector(
    (state) => state.invoice.attachedWorkShiftJournalIds
  )

  const form = useForm({
    name: 'invoice',
    onSubmit: onSubmit,
    defaultValues: formValues,
    validator: (values) =>
      invoiceProductsAndServicesValidate(
        values,
        registeredUsersList,
        attachedWorkShiftJournalIds
      ),
    keepEmptyValuesOnSubmit: false
  })

  useEffect(() => {
    form.reset(formValues)
  }, [formValues])

  const {
    control,
    getValues,
    formState: { errors }
  } = form
  const watchRows = useWatch({
    fields: 'rows',
    control: control
  })
  const country = useWatch({
    fields: 'country',
    control: control
  })
  const rowState = useFieldArray({ name: 'rows', control })
  const { fields: rowFields } = rowState

  const [attemptingSubmit, setAttemptingSubmit] = React.useState(false)

  function onSubmit(values) {
    values.vat = 0
    // KAS-356 If the invoice row is expense, don't need to calculate the vat
    // vat is calculated when adding expense to invoice
    values.rows.forEach((row) => {
      if (row?.vat && row.invoice_expense_id.toString() !== '-1') {
        row.vat = row.vat
      } else {
        row.vat = calculateVat(
          parseFloat(row.quantity),
          parseFloat(row.quantity_price),
          row.vat_percent,
          values.invoice_price_selection,
          row.invoice_expense_id.toString() !== '-1',
          row.invoice_allowance_id.toString() !== '-1'
        )
      }
      values.vat += row.vat
    })

    goNext(values)
  }

  function removeRow(index) {
    rowState.remove(index)
  }

  function addRow(values) {
    if (
      values.vat_percent === 'building_service' ||
      values.vat_percent === 'vat_abroad'
    ) {
      values.vat_percent = 0
    }
    rowState.append(values)
  }

  function setValue(name, value) {
    form?.setValue(name, value)
  }

  function clearErrors(fields) {
    if (fields) {
      form?.clearErrors(fields)
    } else {
      form?.clearErrors()
    }
  }

  const isExpenseAllowanceRow = useMemo(
    () =>
      rowFields.some(
        (row) =>
          row.invoice_expense_id.toString() !== '-1' ||
          row.invoice_allowance_id.toString() !== '-1'
      ),
    [rowFields]
  )

  React.useEffect(() => {
    if (attemptingSubmit && !isEmpty(errors)) {
      setAttemptingSubmit(false)
    }
  }, [attemptingSubmit, errors])

  React.useEffect(() => {
    dispatch(getInvoiceUserEmail(0))
    dispatch(getUserCompanyUpdateInfo())
    dispatch(getJournal())
  }, [])

  // KAS-459 Group invoice
  const [groupEmailCount, setGroupEmailCount] = React.useState(1)
  const groupInvoiceRowState = useFieldArray({
    name: 'groupInvoiceRow',
    control
  })
  const { fields: groupInvoiceFields } = groupInvoiceRowState

  const [warnGroupAmount, setWarnGroupAmount] = React.useState('')

  // KAS-417 Quick pay bug
  const watch = useWatch({
    fields: [
      'total_sum',
      'groupTotalAmount',
      'groupInvoiceRow',
      'quick_pay',
      'groupInvoice',
      'vat_selection',
      'customer_type',
      'rows'
    ],
    control
  })
  const totalInvoiceInputSum = parseFloat(watch.total_sum)
  const groupTotalAmount = parseFloat(watch.groupTotalAmount)
  const quickPay = watch.quick_pay

  const isGroupAmountEmpty = useMemo(() => {
    const areAllGroupAmountsFilled = watch.groupInvoiceRow
      ? watch.groupInvoiceRow.every((row) => row.groupAmount > 0)
      : false
    return areAllGroupAmountsFilled
  }, [watch.groupInvoiceRow])

  const calculateGroupRowAmount = () => {
    const openedRowsCount = watch.groupInvoiceRow.length
    const groupInvoiceRow = watch.groupInvoiceRow

    let adjustedRowCount = 0

    const sumOfTouchedRows = groupInvoiceRow.reduce((acc, row) => {
      if (parseFloat(row.groupAmount) > 0) {
        adjustedRowCount += 1
        return acc + parseFloat(row.groupAmount)
      } else return acc
    }, 0)

    if (openedRowsCount > 0) {
      let amountToDistribute =
        (totalInvoiceInputSum - sumOfTouchedRows) /
        (openedRowsCount - adjustedRowCount)
      amountToDistribute = parseFloat(amountToDistribute.toFixed(2))

      for (let i = 0; i < openedRowsCount; i++) {
        if (
          !parseFloat(groupInvoiceRow[i].groupAmount) ||
          parseFloat(groupInvoiceRow[i].groupAmount) === 0
        ) {
          setValue(`groupInvoiceRow.${i}.groupAmount`, amountToDistribute)
        }
      }

      // Adjust the last row to receive the maximum amount if rounding issue occurs
      const lastRowIndex = openedRowsCount - 1
      const totalAllocatedAmount =
        (openedRowsCount - adjustedRowCount - 1) * amountToDistribute
      const remainingAmount =
        totalInvoiceInputSum - sumOfTouchedRows - totalAllocatedAmount

      if (
        parseFloat(groupInvoiceRow[lastRowIndex].groupAmount) === 0 ||
        !groupInvoiceRow[lastRowIndex].groupAmount
      ) {
        setValue(`groupInvoiceRow.${lastRowIndex}.groupAmount`, remainingAmount)
      }
    }
    clearErrors('groupInvoiceRow')
    clearErrors('groupTotalAmount')
  }

  const removeGroupInvoiceRow = (index) => {
    groupInvoiceRowState.remove(index)
    setGroupEmailCount((prevCount) => prevCount - 1)
    clearErrors('groupInvoiceRow')
    clearErrors('groupTotalAmount')
  }

  const addGroupInvoiceRow = () => {
    if (groupTotalAmount < totalInvoiceInputSum && groupEmailCount < 6) {
      const newRow = {
        groupEmail: '',
        groupAmount: ''
      }
      groupInvoiceRowState.append(newRow)
      setGroupEmailCount((prevCount) => prevCount + 1)
      clearErrors('groupInvoiceRow')
    } else {
      if (groupTotalAmount > totalInvoiceInputSum) {
        setWarnGroupAmount('exceeds')
      } else {
        setWarnGroupAmount('reached')
      }
    }
  }

  const isQuickPay = quickPay ? true : false

  const resetGroupInvoice = () => {
    const openedRowsCount = watch.groupInvoiceRow.length

    for (let i = 0; i < openedRowsCount; i++) {
      setValue(`groupInvoiceRow.${i}.groupAmount`, '')

      if (i !== 0) {
        setValue(`groupInvoiceRow.${i}.groupEmail`, '')
      }
    }
    setValue('groupTotalAmount', 0)
    clearErrors('groupInvoiceRow')
    clearErrors('groupTotalAmount')
  }

  React.useEffect(() => {
    if (getRegisteredUserSuccess) {
      watch.groupInvoiceRow.forEach((row, index) => {
        if (registeredUsersList.some((obj) => obj.email === row.groupEmail)) {
          clearErrors(`groupInvoiceRow.${index}.groupEmail`)
        }
      })
    }
  }, [getRegisteredUserSuccess])

  React.useEffect(() => {
    if (changeCustomerType && disable_building_service) {
      dispatch(changeVATPercent(25.5, watchRows.rows))
    }
  }, [changeCustomerType, disable_building_service])

  const errorMessage = !!warnGroupAmount
    ? warnGroupAmount === 'reached'
      ? t('validation.groupTotalAmountLimitReached')
      : t('validation.groupTotalAmountLimitExceeds')
    : errors?.rows ===
        'Invoice cannot contain both positive and negative rows.' ||
      errors?.rows === 'Credit note amount cannot be positive.'
    ? t('invoice.msg2')
    : errors?.rows === 'Quick pay exceeded'
    ? t('invoice.quickPay')
    : errors?.rows === 'Quick pay is not allowed for negative invoices.'
    ? t('invoice.msg3')
    : null

  const sharedClasses = {
    switch: classes.switch
  }

  return (
    <div>
      <Form {...form}>
        <div className="row">
          <div className="dashboard-content-header">
            <div className="col-xs-12 col-sm-12 col-lg-12">
              <div
                className="panel panel-default"
                style={{ boxShadow: 'none' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: mobileView ? 'column-reverse' : 'column'
                  }}
                >
                  <div id="invoice-items-scroll-section">
                    <h3
                      style={{
                        textAlign: 'center',
                        fontSize: mobileView && '20px',
                        fontWeight: '600',
                        color: '#000',
                        textTransform: 'none',
                        marginBottom: mobileView && '-20px'
                      }}
                    >
                      {t('invoice.row')}
                    </h3>
                  </div>
                  <Field
                    name="vat_selection"
                    inputType="select"
                    className={classNames(classes.select2, classes.resize)}
                    style={{ marginTop: '-20px' }}
                    control={control}
                    onChange={(e) => {
                      dispatch(changeVATPercent(e.target.value, watchRows.rows))
                    }}
                  >
                    <MenuItem value={25.5}>
                      <span
                        style={{
                          width: '100%',
                          fontSize: 'clamp(10px, 2vw, 12px)' // 10px min, 12px max & 2vw responsive scalling based on width
                        }}
                      >
                        {t('invoice.vat_25_5')}
                      </span>
                    </MenuItem>
                    <MenuItem value={24}>
                      <span
                        style={{
                          width: '100%',
                          fontSize: 'clamp(10px, 2vw, 12px)'
                        }}
                      >
                        {t('invoice.vat_24')}
                      </span>
                    </MenuItem>
                    <MenuItem
                      value="building_service"
                      disabled={disable_building_service}
                    >
                      <span
                        style={{
                          width: '100%',
                          fontSize: 'clamp(10px, 2vw, 12px)'
                        }}
                      >
                        {t('invoice.builderCheckBox')}
                      </span>
                    </MenuItem>
                    <MenuItem value={14}>
                      <span
                        style={{
                          width: '100%',
                          fontSize: 'clamp(10px, 2vw, 12px)'
                        }}
                      >
                        {t('invoice.vat_14')}
                      </span>
                    </MenuItem>
                    <MenuItem value={10}>
                      <span
                        style={{
                          width: '100%',
                          fontSize: 'clamp(10px, 2vw, 12px)'
                        }}
                      >
                        {t('invoice.vat_10')}
                      </span>
                    </MenuItem>
                    <MenuItem value={0} disabled={disable_building_service}>
                      <span
                        style={{
                          width: '100%',
                          fontSize: 'clamp(10px, 2vw, 12px)'
                        }}
                      >
                        {t('invoice.vat_0')}
                      </span>
                    </MenuItem>
                    <MenuItem
                      value="vat_abroad"
                      disabled={
                        parseInt(country.country) !== 1 &&
                        !disable_building_service
                          ? false
                          : true
                      }
                    >
                      <span
                        style={{
                          width: '100%',
                          fontSize: 'clamp(10px, 2vw, 12px)'
                        }}
                      >
                        {t('invoice.vat_abroad')}
                      </span>
                    </MenuItem>
                  </Field>
                </div>
                <WatchField control={control} fields={['customer_type']}>
                  {(watch) => (
                    <div style={{ padding: mobileView ? '0' : '0 20px' }}>
                      {mobileView ? (
                        <InvoiceInputMobileComponent
                          fields={rowFields}
                          addRow={addRow}
                          removeRow={removeRow}
                          setValue={setValue}
                          control={control}
                          clearErrors={clearErrors}
                          getValues={getValues}
                          shouldDisableVat0={
                            watch.customer_type === 'private_person'
                          }
                          groupInvoiceFields={groupInvoiceFields}
                          addGroupInvoiceRow={addGroupInvoiceRow}
                          removeGroupInvoiceRow={removeGroupInvoiceRow}
                          groupEmailCount={groupEmailCount}
                          totalInvoiceInputSum={totalInvoiceInputSum}
                          groupTotalAmount={groupTotalAmount}
                          isExpenseAllowanceRow={isExpenseAllowanceRow}
                          calculateGroupRowAmount={calculateGroupRowAmount}
                          isQuickPay={isQuickPay}
                          resetGroupInvoice={resetGroupInvoice}
                          isGroupAmountEmpty={isGroupAmountEmpty}
                          classes={sharedClasses}
                        />
                      ) : (
                        <InvoiceInputTable
                          fields={rowFields}
                          addRow={addRow}
                          removeRow={removeRow}
                          setValue={setValue}
                          control={control}
                          clearErrors={clearErrors}
                          getValues={getValues}
                          shouldDisableVat0={
                            watch.customer_type === 'private_person'
                          }
                          groupInvoiceFields={groupInvoiceFields}
                          addGroupInvoiceRow={addGroupInvoiceRow}
                          removeGroupInvoiceRow={removeGroupInvoiceRow}
                          groupEmailCount={groupEmailCount}
                          totalInvoiceInputSum={totalInvoiceInputSum}
                          groupTotalAmount={groupTotalAmount}
                          isExpenseAllowanceRow={isExpenseAllowanceRow}
                          calculateGroupRowAmount={calculateGroupRowAmount}
                          isQuickPay={isQuickPay}
                          resetGroupInvoice={resetGroupInvoice}
                          isGroupAmountEmpty={isGroupAmountEmpty}
                          classes={sharedClasses}
                        />
                      )}
                    </div>
                  )}
                </WatchField>
              </div>
            </div>
          </div>
        </div>
      </Form>
      <div className={classes.switches}>
        <AddCostContainer
          invoiceFormControl={control}
          getValuesInvoiceForm={getValues}
          classes={sharedClasses}
        />
        <AddAllowanceContainer
          invoiceFormControl={control}
          getValuesInvoiceForm={getValues}
          classes={sharedClasses}
        />
      </div>
      <Form {...form}>
        <WorkshiftJournalContainer
          invoiceFormControl={control}
          isEditInvoice={isEdit}
          errors={errors}
          attachedWorkShiftJournalIds={attachedWorkShiftJournalIds}
          classes={sharedClasses}
        />
        <div
          style={{
            display: 'flex',
            gap: '15px',
            justifyContent: mobileView ? 'center' : 'flex-end',
            margin: '40px 10px 15px'
          }}
        >
          <Button
            className={classNames(classes.buttonBack, classes.button)}
            onClick={() => goBack(form.getValues())}
          >
            {t('asiakkaat.back')}
          </Button>
          <Button
            className={classNames(classes.buttonNext, classes.button)}
            type="submit"
            onClick={() => setAttemptingSubmit(true)}
          >
            {t('asiakkaat.next')}{' '}
            <span
              style={{
                display: 'inline-block',
                transform: 'scaleX(1.5)',
                fontSize: '18px',
                letterSpacing: '-2px'
              }}
            >
              &nbsp; &gt;
            </span>
          </Button>
        </div>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          open={!!errorMessage}
          autoHideDuration={4000}
          onClose={() => {
            if (!!warnGroupAmount) setWarnGroupAmount('')
          }}
        >
          <MySnackbarContentWrapper variant="error" message={errorMessage} />
        </Snackbar>
      </Form>
    </div>
  )
}

export default withStyles(styles)(InvoicedProducts)
